import { Role } from '@app/utils/protected-routes.utils';

import { Permission } from './permissions';

/**
 * Direct permission mappings for each role
 * Roles inherit permissions from roles specified in ROLE_HIERARCHY
 * Only define direct permissions here - inheritance is handled automatically
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  // Base role - view-only access to investigations
  [Role.REVIEWER]: [
    Permission.INVESTIGATIONS_VIEW,
    Permission.EVENTS_VIEW,
    Permission.EVENTS_VIEW_SOURCES,
    Permission.EVENTS_VIEW_FACETS,
    Permission.AGREEMENT_VIEW,
    Permission.REPORTS_VIEW,
  ],

  // Base role - EDR can create quick investigations
  [Role.EDR]: [
    // EDR has minimal permissions - just quick investigation creation
    // Will be expanded as needed
    Permission.REPORTS_VIEW,
  ],

  // Investigator - create and manage investigations
  [Role.INVESTIGATOR]: [
    Permission.INVESTIGATIONS_CREATE,
    Permission.INVESTIGATIONS_UPDATE_STATUS,
    Permission.INVESTIGATIONS_VIEW_MY,
    Permission.TOKEN_LEDGER_VIEW,
  ],

  // Asset Provider - manage assets and agents
  [Role.ASSET_PROVIDER]: [
    Permission.ASSETS_VIEW,
    Permission.ASSETS_CREATE,
    Permission.ASSETS_UPDATE,
    Permission.AGENTS_VIEW,
    Permission.AGENTS_CREATE,
    Permission.AGENTS_UPDATE,
    Permission.AGENTS_DELETE,
    Permission.TRACE_JOBS_VIEW,
    Permission.TRACE_JOBS_UPDATE,
    Permission.REPORTS_VIEW,
  ],

  // Admin - full platform management (non-ops)
  [Role.ADMIN]: [
    Permission.USERS_VIEW,
    Permission.USERS_CREATE,
    Permission.USERS_UPDATE,
    Permission.USERS_DELETE,
    Permission.USERS_LOCK,
    Permission.USERS_MANAGE_ROLES,
    Permission.API_KEYS_VIEW,
    Permission.API_KEYS_CREATE,
    Permission.API_KEYS_DELETE,
    Permission.APPLICATIONS_VIEW,
    Permission.APPLICATIONS_MANAGE,
    Permission.INTEGRATIONS_VIEW,
    Permission.TOKEN_LEDGER_VIEW,
    Permission.CONFIG_VIEW,
    Permission.CONFIG_MANAGE,
    Permission.AGREEMENT_ACCEPT,
    Permission.AUDIT_VIEW_TENANT,
  ],

  // Operator - operations management
  [Role.OPERATOR]: [
    Permission.OPS_MANAGEMENT_ACCESS,
    Permission.COMPANIES_VIEW,
    Permission.TENANTS_VIEW,
    Permission.TENANTS_ACTIVATE,
  ],

  // Super Admin - full system access
  [Role.SUPER_ADMIN]: [
    Permission.COMPANIES_MANAGE,
    Permission.TENANTS_MANAGE,
    Permission.CONFIG_TYPES_MANAGE,
    Permission.AUDIT_VIEW_ALL,
    Permission.AUDIT_VIEW_TENANT,
  ],
};

/**
 * Feature-level permission requirements
 * Defines what permissions are needed to access specific features
 */
export enum Feature {
  // Pages
  INVESTIGATIONS_PAGE = 'investigations.page',
  USER_MANAGEMENT_PAGE = 'userManagement.page',
  APPLICATIONS_PAGE = 'applications.page',
  INTEGRATIONS_PAGE = 'integrations.page',
  TOKEN_LEDGER_PAGE = 'tokenLedger.page',
  REPORTS_PAGE = 'reports.page',
  ASSETS_PAGE = 'assets.page',
  API_KEYS_PAGE = 'apiKeys.page',
  OPS_MANAGEMENT_PAGE = 'ops.page',
  AUDIT_PAGE = 'audit.page',

  // Investigation features
  INVESTIGATIONS_NEW_BUTTON = 'investigations.newButton',
  INVESTIGATIONS_MY_BUTTON = 'investigations.myButton',

  // User management features
  USER_CREATE_BUTTON = 'userManagement.createButton',
  USER_EDIT_ACTION = 'userManagement.editAction',
  USER_DELETE_ACTION = 'userManagement.deleteAction',
}

/**
 * Maps features to required permissions
 * A user needs at least one of the listed permissions to access the feature
 */
export const FEATURE_PERMISSIONS: Record<Feature, Permission[]> = {
  // Pages - accessible if user has ANY of these permissions
  [Feature.INVESTIGATIONS_PAGE]: [Permission.INVESTIGATIONS_VIEW],
  [Feature.USER_MANAGEMENT_PAGE]: [Permission.USERS_VIEW],
  [Feature.APPLICATIONS_PAGE]: [Permission.APPLICATIONS_VIEW],
  [Feature.INTEGRATIONS_PAGE]: [Permission.INTEGRATIONS_VIEW],
  [Feature.TOKEN_LEDGER_PAGE]: [Permission.TOKEN_LEDGER_VIEW],
  [Feature.REPORTS_PAGE]: [Permission.REPORTS_VIEW],
  [Feature.ASSETS_PAGE]: [Permission.ASSETS_VIEW],
  [Feature.API_KEYS_PAGE]: [Permission.API_KEYS_VIEW],
  [Feature.OPS_MANAGEMENT_PAGE]: [Permission.OPS_MANAGEMENT_ACCESS],
  [Feature.AUDIT_PAGE]: [Permission.AUDIT_VIEW_ALL],

  // Investigation features
  [Feature.INVESTIGATIONS_NEW_BUTTON]: [Permission.INVESTIGATIONS_CREATE],
  [Feature.INVESTIGATIONS_MY_BUTTON]: [Permission.INVESTIGATIONS_VIEW_MY],

  // User management features
  [Feature.USER_CREATE_BUTTON]: [Permission.USERS_CREATE],
  [Feature.USER_EDIT_ACTION]: [Permission.USERS_UPDATE],
  [Feature.USER_DELETE_ACTION]: [Permission.USERS_DELETE],
};
